import { useState, useCallback } from 'react';
import { alertApi, AlertRecipient } from '../services/alertApi';

interface UseAlertRecipientsReturn {
  // State
  recipients: AlertRecipient[];
  loading: boolean;
  error: string | null;
  
  // Actions
  getAlertRecipients: (_alertId: number) => Promise<AlertRecipient[]>;
  addRecipient: (_email: string, _name?: string) => void;
  removeRecipient: (_email: string) => void;
  
  // Utility
  clearError: () => void;
}

export const useAlertRecipients = (): UseAlertRecipientsReturn => {
  const [recipients, setRecipients] = useState<AlertRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get recipients for a specific alert
  const getAlertRecipients = useCallback(async (_alertId: number): Promise<AlertRecipient[]> => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('useAlertRecipients.getAlertRecipients called with alertId:', _alertId);
      const response = await alertApi.getAlertRecipients(_alertId);
      console.log('useAlertRecipients.getAlertRecipients response:', response);
      
      setRecipients(response);
      return response;
    } catch (err: any) {
      console.error('Error in getAlertRecipients:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch alert recipients';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);



  // Add a new recipient to the local state
  const addRecipient = useCallback((_email: string, _name?: string) => {
    const newRecipient: AlertRecipient = {
      alertRecipientId: Date.now(), // Temporary ID for new recipients
      email: _email,
      name: _name || _email.split('@')[0], // Use email prefix as name if not provided
      isActive: true
    };
    
    setRecipients(prevRecipients => {
      // Check if recipient already exists
      const exists = prevRecipients.some(r => r.email.toLowerCase() === _email.toLowerCase());
      if (exists) {
        return prevRecipients;
      }
      return [...prevRecipients, newRecipient];
    });
  }, []);

  // Remove a recipient from the local state
  const removeRecipient = useCallback((_email: string) => {
    setRecipients(prevRecipients => 
      prevRecipients.filter(r => r.email.toLowerCase() !== _email.toLowerCase())
    );
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    recipients,
    loading,
    error,
    
    // Actions
    getAlertRecipients,
    addRecipient,
    removeRecipient,
    
    // Utility
    clearError,
  };
};
